import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { Search, XIcon } from 'lucide-react';
import { Input, Col, Row, Button, Label, Separator, List, ListItem, Checkbox, Badge, cn } from 'twyne-new.ui';

const propTypes = {
  data: PropTypes.any,
  defaultValue: PropTypes.any,
  onChange: PropTypes.func,
};

export default class FilterConfigure extends React.Component {
  state = {
    suboptions: [],
    selectedField: null,
    selectedLabel: null,
    selected: {},
  }

  componentDidUpdate() {
    const filters = Object.values(this.props.defaultValue || {})
    const shouldUpdate = filters.some(filter => Array.isArray(filter))
    if (shouldUpdate) {
      this.parseState()
    }
  }

  componentDidMount() {
    this.parseState()
  }

  async parseState() {
    const { defaultValue, data: { options } } = this.props
    const filters = Object.entries(defaultValue || {})

    const getReportConfiguration = (options, filterKey) => {
      const option = options.find(({ value }) => value === filterKey)
      if (option) {
        const fetchSuboptions = option?.options
        const label = option?.label?.props?.children
        return { label, fetchSuboptions }
      }
      return null
    }

    const createItem = (suboptions, filterValue) => {
      const suboption = suboptions.find(({ value }) => value === filterValue)
      return {
        value: filterValue,
        label: suboption?.label,
        getValue: suboption?.getValue || (() => filterValue),
        getLabel: suboption?.getLabel || (() => suboption?.label),
      }
    }

    const selected = {}

    for (const [key, values] of filters) {
      if (!Array.isArray(values)) {
        this.setState({ selected: defaultValue })
        return;
      }

      if (!values.length) {
        continue;
      }

      const option = getReportConfiguration(options, key)
      if (!option) {
        continue;
      }

      await option.fetchSuboptions().then(suboptions => {
        const items = values.map(value => createItem(suboptions, value))
        selected[key] = { label: option.label, items }
      })
    }

    this.setState({ selected }, () => this.props.onChange(selected))
  }

  handleChangeSelected = selected => {
    this.setState({ selected }, () => this.props.onChange(selected))
  }

  handleSelectField = async ({ label, options, value }) => {
    const selectedLabel = label.props && (label.props.children || '')
    const suboptions = typeof options === 'function' ? await options() : options
    this.setState({ suboptions, selectedField: value, selectedLabel })
  }

  isValueSelected = value => {
    const { selected, selectedField, selectedLabel } = this.state
    const fieldData = selected[selectedField] = selected[selectedField] || { label: selectedLabel, items: [] }
    return !!fieldData.items.find(item => value == item.value)
  }

  handleSelectValue = (value, label, field, getValue) => action => {
    const { selected, selectedLabel } = this.state
    const fieldData = selected[field] = selected[field] || { label: selectedLabel, items: [] }

    if (action) {
      fieldData.items.push({ value, label, getValue })
    } else {
      const idx = fieldData.items.findIndex(item => value == item.value)
      idx !== -1 && fieldData.items.splice(idx, 1)
    }

    this.handleChangeSelected(selected)
  }

  ValueItem = ({ value, label, getValue, getLabel }) => <>
    <Checkbox
      onChange={this.handleSelectValue(value, label, this.state.selectedField, getValue)}
      value={this.isValueSelected(value)}
    />
    {getLabel()}
  </>

  PinItem = ({ onClose, children }) => {
    const handleClose = () => {
      onClose && onClose()
    }

    return <Badge variant='light' size='lg'>
      <div className='flex justify-between items-center gap-2'>
        {children}
        <XIcon size='12' className='cursor-pointer hover:twy-text-base-accent' onClick={handleClose} />
      </div>
    </Badge>
  }

  SelectedValues = ({ field }) => {
    const { selected } = this.state
    return <div className='flex flex-wrap gap-2'>
      {selected[field].items.map((item, idx) => {
        const { value, label } = item
        return <>
          <this.PinItem
            key={idx}
            onClose={this.handleSelectValue.call(this, value, label, field).bind(this, false)}
          >
            {[value, label].join(' - ')}
          </this.PinItem>
        </>
      })}
    </div>
  }

  handleClearFilter = () => {
    this.setState({ selected: {} }, () => this.props.onChange({}))
  }

  handleClearLabelFilter = selectedLabel => {
    const selectedLabelFields = Object.values(this.state.selected).find(item => item.label == selectedLabel)
    selectedLabelFields.items = []
    const selectedState = { ...this.state.selected, ...selectedLabelFields }
    this.setState({ selected: selectedState }, () => this.props.onChange({}))
  }

  handleSelectAll = (check, searchItems) => {
    const { selectedLabel, selectedField } = this.state

    this.handleClearLabelFilter(selectedLabel)
    for (const { value, label, getValue } of searchItems) {
      this.handleSelectValue(value, label, selectedField, getValue)(check)
    }
  }

  AppliedFilters = ({ onClearAll, selected }) => <>
    <div className='flex justify-between items-center mb-2'>
      <span className='twy-text-base-accent text-lg'>Applied Filters</span>
      <Button size='sm' onClick={onClearAll} variant='light' color='accent'>Clear all</Button>
    </div>
    <div className='flex flex-col gap-4 h-[200px] overflow-y-auto'>
      {Object.keys(selected).map((field, idx) => {
        const fieldLabel = selected[field] && selected[field].label
        if (selected[field] && selected[field].items && selected[field].items.length > 0) {
          return <div key={idx}>
            <Label className="twy-text-base-accent text-md mb-1">{fieldLabel}</Label>
            <this.SelectedValues field={field} />
          </div>
        }
      })}
    </div>
  </>

  render() {
    const { options } = this.props.data
    const { suboptions, selected } = this.state

    return <>
      <Row className='gap-0'>
        <Col size='3' className='border-r twy-border-base pt-2 h-[calc(100vh-18rem)]'>
          <ListSearch
            items={options}
            onSelect={this.handleSelectField}
            height={600}
            searchClassName='pr-2'
          />
        </Col>
        <Col size='9'>
          <Row className='pt-2'>
            <Col>
              <ListSearch
                className=''
                items={suboptions}
                itemRender={this.ValueItem}
                selected={this.state.selected}
                selectedLabel={this.state.selectedLabel}
                onSelectAll={this.handleSelectAll}
                checkboxList
                searchClassName='ml-2 pr-2'
              />
            </Col>
            <Col>
              <Separator className='mx-0 my-2' />
            </Col>
            <Col className='pl-4'>
              <this.AppliedFilters onClearAll={this.handleClearFilter} selected={selected} />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  }
}

function ListSearch({ items, onSelect, itemRender, checkboxList, selected, selectedLabel, onSelectAll, className, height = 350, searchClassName = '' }) {
  const [active, setActive] = useState(null)
  const [searchItems, setSearchItems] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectAll, setSelectAll] = useState(false)

  const determineSelectedAll = () => {
    if (!selectedLabel) {
      return false
    }

    const selectedItems = Object.values(selected).find(item => item.label == selectedLabel)
    if (!selectedItems || !selectedItems.items || !selectedItems.items.length) {
      return false
    }

    if (items.length != selectedItems.items.length) {
      return false
    }

    return true
  }

  useEffect(() => {
    setSelectAll(determineSelectedAll())
  }, [JSON.stringify(selected), selectedLabel])

  useEffect(() => {
    setActive(null)
    setSearchItems(items)
    setSearchTerm('')
  }, [items])

  useEffect(() => {
    setSearchItems(items.filter(({ label, value }) => {
      if (!searchTerm) return true

      const uiLabel = label.props ? label.props.children : `[#${value}] - ${label}`
      const rgx = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')

      if (uiLabel.match(rgx)) return true

      if (`${value} ${label}`.match(rgx)) return true

      return false
    }))
  }, [searchTerm, items])

  const handleClick = item => () => {
    setActive(item.value)
    onSelect && onSelect(item)
  }

  const handleSelectAll = check => {
    setSelectAll(check)
    onSelectAll(check, searchItems)
  }

  return <div className={className}>
    <SearchInput value={searchTerm} onChange={setSearchTerm} className={searchClassName} />
    <Separator className='mx-0 my-2' />
    <div className={cn('overflow-y-auto', `h-[${height}px]`)}>
      {checkboxList ? <>
        <ChildList
          checkboxList={checkboxList}
          searchItems={searchItems}
          active={active}
          onClick={handleClick}
          onClickAll={handleSelectAll}
          selectAll={selectAll}
          itemRender={itemRender}
        />
      </> : <>
        <ModuleList
          searchItems={searchItems}
          active={active}
          onClick={handleClick}
          itemRender={itemRender}
        />
      </>}
    </div>
  </div>
}

const ModuleList = ({ searchItems, active, onClick, itemRender }) => <>
  <List className='px-0! pt-0!'>
    {searchItems.map((item, idx) => <ListItemModule key={idx} active={active === item.value} onClick={onClick(item)}>
      {itemRender ? itemRender(item) : item.label}
    </ListItemModule>)}
  </List>
</>

const ChildList = ({ checkboxList, searchItems, active, onClick, onClickAll, selectAll, itemRender }) => <>
  {(checkboxList && searchItems.length > 0) && <>
    <ListItemChild
      onClick={onClickAll}
      value={selectAll}
      isSelectAll
    >
      Select all
    </ListItemChild>
  </>}
  {searchItems.map((item, idx) => <ListItemChild index={idx} key={idx} value={active === item.value} onClick={onClick(item)}>
    {itemRender ? itemRender(item) : item.label}
  </ListItemChild>)}
</>

const SearchInput = ({ value, onChange, className = '' }) => <>
  <div className={cn('relative w-full sticky top-0', className)}>
    <Input
      placeholder='Search...'
      className='pl-8 border-0 focus:bg-gray-200 focus:dark:bg-gray-950'
      value={value}
      onChange={e => onChange(e.target.value)}
    />
    <Search size='18' className='absolute left-2 top-1/2 -translate-y-1/2' />
  </div>
</>

const ListItemChild = ({ value, onClick, children, isSelectAll, index }) => <>
  <div className={cn('flex items-center gap-4 p-3', isSelectAll && 'border-b twy-border-base', index % 2 === 0 && 'twy-bg-base')}>
    {isSelectAll && <Checkbox onChange={onClick} value={value} />}
    {children}
  </div>
</>

const ListItemModule = ({ children, active, ...props }) => <>
  <ListItem
    className={cn('px-0 p-3 mr-2', active ? 'twy-subtle-neutral twy-text-base-accent' : '')}
    {...props}>
    {children}
  </ListItem>
</>

FilterConfigure.propTypes = propTypes
FilterConfigure.defaultProps = {}
